import { Configuration } from '@Types';
import { SensorConfigFormShape } from '@Types/configurations.types';
import { generateConfigurationCoords } from '@Utils';
import { SensorType } from '@innovyze/shared-utils';

const SENSOR_TYPES = new Map(
	Object.entries(SensorType).map(type => [type[0].toLowerCase(), type[1]]),
);

export const stringifyCoord = (coord: number) =>
	coord.toLocaleString('en', {
		useGrouping: false,
		minimumFractionDigits: 1,
		maximumFractionDigits: 14,
	});

export const stringifyLocationCoords = (coordinates: number[]) => {
	let ret = '';
	if (coordinates && coordinates?.length) {
		const [lng, lat] = coordinates;
		ret = `${stringifyCoord(lat)}, ${stringifyCoord(lng)}`;
	}
	return ret;
};

const generateInterpolationValues = (configuration: Configuration) => {
	let enabled = '';
	let method = '';

	if (configuration?.interpolation) {
		enabled = configuration?.interpolation.enabled ? 'true' : 'false';
		method = configuration?.interpolation.method ?? '';
	}

	return { enabled, method };
};

const generateConfigurationValues = (configuration: Configuration) => {
	let type = configuration?.sensorType ?? '';
	type = SENSOR_TYPES.get(type.toLowerCase().replace(/ /g, '')) ?? '';
	if (!type) type = SensorType.Other;

	let tankType = configuration?.tankType;
	if (!tankType) {
		if (configuration?.tankCurveId) {
			tankType = 'non-cylindrical';
		} else {
			tankType = 'cylindrical';
		}
	}
	return {
		type,
		id: configuration?.sensorId ?? '',
		table: configuration?.sensorTable ?? '',
		dateField: configuration?.sensorDateField ?? '',
		timeField: configuration?.sensorTimeField ?? '',
		channelField: configuration?.sensorChannelField ?? '',
		unitConversion: configuration?.sensorUnitConversion ?? '',
		idField: configuration?.sensorIdField ?? '',
		tankType,
		tankOffset:
			configuration.tankOffset === undefined
				? ''
				: configuration.tankOffset.toString(),
		tankDiameter:
			configuration.tankDiameter === undefined
				? ''
				: configuration.tankDiameter.toString(),
		isPhysical:
			configuration.isPhysical === undefined
				? 'true'
				: configuration.isPhysical.toString(),
	};
};

const generateMiscValues = (configuration: Configuration) => {
	let dataSourceType =
		configuration?.dataSourceType ?? configuration?.sourceType ?? 'ODBC';
	// Added specific case for records with dataSourceType='' value since ?? will treat it as defined and keep the '' value.
	if (dataSourceType === '') dataSourceType = 'ODBC';

	return {
		collectionInterval: configuration?.collectionInterval
			? configuration.collectionInterval
			: '',
		dataSourceId: configuration?.dataSourceId ?? '',
		dataSourceType,
		unit: configuration?.unit ?? '',
		assetId: configuration?.assetId ?? '',
		tankCurveId: configuration?.tankCurveId ?? '',
		tagValue: configuration?.tagValue ?? '',
	};
};

export const mapDataToForm = (
	configuration: Configuration,
): SensorConfigFormShape => {
	const coords = stringifyLocationCoords(
		generateConfigurationCoords(
			configuration.longitude,
			configuration.latitude,
		),
	);
	const interpolationValues = generateInterpolationValues(configuration);
	const configurationValues = generateConfigurationValues(configuration);
	const miscValues = generateMiscValues(configuration);

	return {
		sensorType: configurationValues.type,
		sensorId: configurationValues.id,
		sensorAlias: configuration.sensorAlias ?? configuration.sensorId ?? '',
		collectionInterval: miscValues.collectionInterval,
		dataSourceId: miscValues.dataSourceId,
		dataSourceType: miscValues.dataSourceType,
		location: coords,
		sensorTable: configurationValues.table,
		sensorDateField: configurationValues.dateField,
		sensorTimeField: configurationValues.timeField,
		sensorChannelField: configurationValues.channelField,
		unit: miscValues.unit,
		sensorUnitConversion: configurationValues.unitConversion,
		sensorIdField: configurationValues.idField,
		assetId: miscValues.assetId,
		tankCurveId: miscValues.tankCurveId,
		interpolationEnabled: interpolationValues.enabled,
		interpolationMethod: interpolationValues.method,
		tagValue: miscValues.tagValue,
		tankType: configurationValues.tankType,
		tankOffset: configurationValues.tankOffset,
		tankDiameter: configurationValues.tankDiameter,
		isPhysical: configurationValues.isPhysical,
	};
};
